import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { MarkdownRenderer } from '../components/editor/MarkdownRenderer'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

/**
 * formatted.markdown からサマリ部分だけを抽出する。
 * 「サマリ」を含む見出し（##等）から次の同レベル以上の見出しの直前までを返す。
 * サマリ見出しが見つからない場合は null（呼び出し側でフォールバック）。
 */
function extractSummary(markdown: string): string | null {
  const lines = markdown.replace(/\r\n?/g, '\n').split('\n')
  let start = -1
  let headingLevel = 0
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^(#{1,6})\s*(.*)$/)
    if (m && /サマリ|要約|Summary/i.test(m[2])) {
      start = i
      headingLevel = m[1].length
      break
    }
  }
  if (start === -1) return null
  const out: string[] = [lines[start]]
  for (let i = start + 1; i < lines.length; i++) {
    const m = lines[i].match(/^(#{1,6})\s/)
    if (m && m[1].length <= headingLevel) break
    out.push(lines[i])
  }
  return out.join('\n').trim()
}

/**
 * 閲覧専用（viewerロール）向けの議事録ページ。
 * 通常の詳細ページとは別UIで、タイトル・日時・参加者と「サマリ」のみを表示する。
 * 文字起こし・録画・アクションアイテム・編集系機能は一切表示しない。
 */
export function ViewerDocumentPage() {
  const { id } = useParams<{ id: string }>()
  const { user, firebaseUser, signOut } = useAuth()
  const [doc, setDoc] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!firebaseUser || !user?.orgId || !id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setError(null)
      try {
        const token = await firebaseUser.getIdToken()
        const res = await fetch(`${API_BASE}/v1/organizations/${user.orgId}/documents/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (res.status === 403 || res.status === 404) {
          throw new Error('この議事録を閲覧する権限がないか、存在しません。')
        }
        if (!res.ok) throw new Error(`読み込みに失敗しました (${res.status})`)
        const data = await res.json()
        if (!cancelled) setDoc(data.document || data)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : '読み込みに失敗しました')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [firebaseUser, user?.orgId, id])

  const markdown: string = doc?.formatted?.markdown || ''
  const summary = markdown ? (extractSummary(markdown) || markdown) : ''

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-surface, #f5f6f8)' }}>
      {/* シンプルヘッダー */}
      <header
        className="flex items-center justify-between"
        style={{
          padding: 'calc(env(safe-area-inset-top) + 12px) 20px 12px',
          background: 'var(--color-base, #fff)',
          borderBottom: '1px solid var(--color-border-light, #e5e7eb)'
        }}
      >
        <Link to="/viewer" style={{ fontFamily: 'var(--font-brand)', fontSize: 20, fontWeight: 700, letterSpacing: '0.02em', color: 'inherit', textDecoration: 'none' }}>
          BluePaper
        </Link>
        <div className="flex items-center gap-3">
          <span className="badge badge-neutral">閲覧専用</span>
          <button className="btn btn-ghost btn-sm" onClick={() => signOut()}>ログアウト</button>
        </div>
      </header>

      <main style={{ maxWidth: 720, margin: '0 auto', padding: '24px 16px calc(env(safe-area-inset-bottom) + 40px)' }}>
        {loading ? (
          <div className="flex items-center justify-center gap-2" style={{ padding: 64 }}>
            <span className="spinner" />
            <span className="text-secondary text-sm">読み込み中...</span>
          </div>
        ) : error ? (
          <div className="card" style={{ padding: 32, textAlign: 'center' }}>
            <p className="text-sm text-secondary">{error}</p>
          </div>
        ) : (
          <div className="card" style={{ padding: 'var(--space-6, 24px)' }}>
            <h1 className="text-lg font-semibold text-primary" style={{ marginBottom: 8 }}>
              {doc?.title || '議事録'}
            </h1>
            <div className="flex items-center gap-3 flex-wrap text-xs text-secondary" style={{ marginBottom: 16 }}>
              {doc?.audit?.createdAt && (
                <span>
                  {(() => {
                    const c = doc.audit.createdAt
                    const d = c?.seconds ? new Date(c.seconds * 1000) : new Date(c?._seconds ? c._seconds * 1000 : c)
                    return isNaN(d.getTime()) ? null : d.toLocaleDateString('ja-JP')
                  })()}
                </span>
              )}
              {Array.isArray(doc?.participants) && doc.participants.length > 0 && (
                <span className="truncate">参加者: {doc.participants.join('、')}</span>
              )}
            </div>
            {summary ? (
              <MarkdownRenderer content={summary} showEditButton={false} />
            ) : (
              <p className="text-sm text-secondary">この議事録にはまだ要約がありません。</p>
            )}
            <p className="text-xs text-tertiary" style={{ marginTop: 24, borderTop: '1px solid var(--color-border-light)', paddingTop: 12 }}>
              閲覧専用アカウントのため、議事録のサマリのみ表示しています。
            </p>
          </div>
        )}
      </main>
    </div>
  )
}
